var RepeaterArranger = require("enyo-ui/RepeaterArranger"),
    kind             = require("enyo/kind"),
    logger           = require("enyo/logger");

require("enyo-ui");

/**
 * [RepeaterGridArranger]{@link enyo-ui.RepeaterGridArranger} is an arranger for
 * [Repeater]{@link enyo-ui.Repeater} aimed at arranging a grid of same-size
 * items.
 *
 * The grid will arrange using the least amount of columns (or rows) needed,
 * while never exceeding the maximum column width.
 *
 * Grid items are expected to implement [FitableSupport]{@link
 * enyo-ui.FitableSupport}, to allow easy calculation of their dimensions.
 *
 * @ui
 * @class   enyo-ui.RepeaterGridArranger
 * @extends enyo-ui.RepeaterArranger
 * @public
 */
module.exports = kind(/** @lends  enyo-ui.RepeaterGridArranger.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.RepeaterGridArranger",

    /**
     * @private
     */
    kind: RepeaterArranger,

    /**
     * A reference to the child control instance used to calculate the layout.
     *
     * @private
     */
    layoutChild: null,

    /**
     * The width of a column in the grid.
     *
     * @type    {number}
     * @default 0
     * @private
     */
    colWidth: 0,

    /**
     * The height of a row in the grid.
     *
     * @type    {number}
     * @default 0
     * @private
     */
    rowHeight: 0,

    /**
     * The maximum amount of columns in the grid.
     *
     * @type    {number}
     * @default 0
     * @private
     */
    maxCols: 0,

    /**
     * The maximum amount of rows in the grid.
     *
     * @type    {number}
     * @default 0
     * @private
     */
    maxRows: 0,

    /**
     * @private
     */
    options: /** @lends  enyo-ui.RepeaterGridArranger.prototype */ {

        /**
         * Sets which axis should be used to divide the viewport into
         * rows/columns.
         *
         * @type    {string}
         * @default "x"
         * @public
         */
        dividingAxis: "x",

        /**
         * Sets the step size with which the amount of rows/columns dividing the
         * viewport should be incremented.
         *
         * For example: A `divisionStep` value of 2 will divide the viewport
         * into 0, 2, 4, 6 etc. columns/rows.
         *
         * @type    {number}
         * @default 1
         * @public
         */
        divisionStep: 1,

        /**
         * Sets an optional offset to apply when dividing the viewport into
         * rows/columns. The offset value must be positive and will only be
         * applied if the viewport can accommodate 1 or more rows/columns.
         *
         * For example: A [`divisionStep`]{@link
         * enyo-ui.RepeaterGridArranger#divisionStep} value of 1, and a
         * `divisionOffset` value of 0.5 will result in 0, 1.5, 2.5, 3.5 etc.
         * columns/rows.
         *
         * @type    {number}
         * @default 0
         * @public
         */
        divisionOffset: 0,

        /**
         * If `dividingAxis` option is set to "x", the `viewportWidth` will be
         * used to calculate the width of grid columns. The arrangement will use
         * the smallest amount of columns that can be drawn within the
         * `viewportWidth` without exceeding `maxColWidth`.
         *
         * By default, we assume the viewport equals the repeaters' boundaries.
         * Override this option if this is not the case.
         *
         * @return {number}  The viewport width in pixels
         * @public
         */
        viewportWidth: function() {
            var node = this.repeater.hasNode();

            return node ? node.clientWidth : 0;
        },

        /**
         * If `dividingAxis` option is set to "y", the `viewportHeight` will be
         * used to calculate the height of grid rows. The arrangement will use
         * the smallest amount of rows that can be drawn within the
         * `viewportHeight` without exceeding `maxRowHeight`.
         *
         * By default, we assume the viewport equals the repeaters' boundaries.
         * Override this option if this is not the case.
         *
         * @return {number}  The viewport height in pixels
         * @public
         */
        viewportHeight: function() {
            var node = this.repeater.hasNode();

            return node ? node.clientHeight : 0;
        },

        /**
         * The maximum width of a column in pixels.
         *
         * @type    {number}
         * @default Infinity
         * @public
         */
        maxColWidth: Infinity,

        /**
         * The maximum height of a row in pixels.
         *
         * @type    {number}
         * @default Infinity
         * @public
         */
        maxRowHeight: Infinity,

        /**
         * The grid will not be allowed to expand beyond this width. Set to
         * `Infinity` if you want the grid to be able to expand indefinitely
         * along the x axis.
         *
         * By default, we assume the maximum width of the grid equals the width
         * of the repeater. Override this option if this is not the case.
         *
         * @return {number}  The maximum width of the grid in pixels
         * @public
         */
        maxGridWidth: function() {
            var node = this.repeater.hasNode();

            return node ? node.clientWidth : 0;
        },

        /**
         * The grid will not be allowed to expand beyond this height. Set to
         * `Infinity` if you want the grid to be able to expand indefinitely
         * along the y axis.
         *
         * By default, we assume the maximum height of the grid equals the
         * height of the repeater. Override this option if this is not the case.
         *
         * @return {number}  The maximum height of the grid in pixels
         * @public
         */
        maxGridHeight: function() {
            var node = this.repeater.hasNode();

            return node ? node.clientHeight : 0;
        },

        /**
         * The grid will not be allowed to expand beyond this amount of columns.
         *
         * @type    {number}
         * @default Infinity
         * @public
         */
        maxCols: Infinity,

        /**
         * The grid will not be allowed to expand beyond this amount of rows.
         *
         * @type    {number}
         * @default Infinity
         * @public
         */
        maxRows: Infinity,

        /**
         * The cells in the grid will be aligned along this axis first, before
         * wrapping to the next col/row.
         *
         * @type    {string}
         * @default "x"
         * @public
         */
        primaryAxis: "x",

        /**
         * Sets the direction along which the items should first be arranged
         * within the cells of the grid.
         *
         *  - A value of "x" will arrange items from left to right first, before
         *    staring on a new row.
         *
         *  - A value of "y" will arrange items from top to bottom first, before
         *    starting a new column.
         *
         * @type    {string}
         * @default "x"
         * @public
         */
        arrangementAxis: "x",

        /**
         * Whether to round values of cell width/height and arrangement
         * transform to integer pixels values.
         *
         * @type    {boolean}
         * @default false
         * @public
         */
        roundPixels: false
    },

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.createLayoutChild();
        };
    }),

    /**
     * Creates a repeaterChild which will only be used to calculate the
     * dimensions of repeater children.
     *
     * By default we don't let the repeaterChild render, because ideally the
     * child should be able to calculate it's own dimensions without needing to
     * be rendered into the DOM.
     *
     * Extend this method if you really need the child to be rendered.
     *
     * @private
     */
    createLayoutChild: function() {
        var repeater       = this.repeater,
            childContainer = repeater.getChildContainer(),
            layoutChildKind,
            layoutChildOptions;

        // Create a placeholder component if possible
        if (repeater.getPlaceholderKind && repeater.getPlaceholderOptions) {
            layoutChildKind    = repeater.getPlaceholderKind();
            layoutChildOptions = repeater.getPlaceholderOptions(layoutChildKind);
        }

        // If that fails, back to a normal child component
        if (!layoutChildKind || !layoutChildOptions) {
            layoutChildKind    = repeater.getChildKind();
            layoutChildOptions = repeater.getChildOptions(layoutChildKind);
        }

        if (layoutChildKind) {
            this.layoutChild = childContainer.createComponent({
                kind:        layoutChildKind,
                classes:     (layoutChildOptions.classes || ""),
                canGenerate: false,
                owner:       childContainer,
                parent:      childContainer,
                repeater:    repeater
            }, layoutChildOptions);
            this.layoutChild.addClass("enyo-ui-repeater__layout-child");
        }
        else {
            logger.error("RepeaterGridArranger expects repeater to define a `childKind`");
        }
    },

    /**
     * Convenience method to replace the current layoutChild. Call this method
     * if for some reason you need another type of layoutChild for your reflows.
     *
     * @public
     */
    resetLayoutChild: function() {
        this.layoutChild && this.layoutChild.destroy();
        this.createLayoutChild();
    },

    /**
     * Calculates the properties of the grid arrangement.
     *
     * @public
     */
    reflow: function() {
        var childContainer = this.repeater.getChildContainer();

        // Container required
        if (!childContainer.hasNode()) {
            return;
        }

        switch (this.getOption("dividingAxis")) {
            case "y":
                this.calcRowHeight();
                this.calcColWidth();
                this.calcMaxCols();
                this.calcMaxRows();
                break;
            case "x":
            default:
                this.calcColWidth();
                this.calcRowHeight();
                this.calcMaxCols();
                this.calcMaxRows();
                break;
        }
    },

    /**
     * Calculate the width of a column.
     *
     * @private
     */
    calcColWidth: function() {
        var viewportWidth,
            maxColWidth,
            step,
            offset,
            colWidth,
            cols;

        switch (this.getOption("dividingAxis")) {
            case "y":
                // This requires the rowHeight has already been calculated
                colWidth = Math.max(0, this.layoutChild.calcWidth(this.rowHeight));
                break;
            case "x":
            default:
                viewportWidth = this.getOption("viewportWidth");
                maxColWidth   = this.getOption("maxColWidth");
                step          = this.getOption("divisionStep");
                offset        = this.getOption("divisionOffset");

                if (viewportWidth <= 0 || maxColWidth <= 0 || step <= 0) {
                    colWidth = 0;
                }
                else {
                    offset   = offset < 0 ? 0 : offset;
                    step     = step <= 0 ? 1 : step;
                    cols     = viewportWidth / maxColWidth;
                    cols     = Math.ceil(cols / step) * step;
                    colWidth = viewportWidth / (cols + offset);
                }
                break;
        }

        this.colWidth = colWidth;
    },

    /**
     * Calculate the height of a row.
     *
     * @private
     */
    calcRowHeight: function() {
        var rowHeight,
            viewportHeight,
            maxRowHeight,
            step,
            offset,
            rows;

        switch (this.getOption("dividingAxis")) {
            case "y":
                viewportHeight = this.getOption("viewportHeight");
                maxRowHeight   = this.getOption("maxRowHeight");
                step           = this.getOption("divisionStep");
                offset         = this.getOption("divisionOffset");

                if (viewportHeight <= 0 || maxRowHeight <= 0 || step <= 0) {
                    rowHeight = 0;
                }
                else {
                    offset    = offset < 0 ? 0 : offset;
                    rows      = viewportHeight / maxRowHeight;
                    rows      = Math.ceil(rows / step) * step;
                    rowHeight = viewportHeight / (rows + offset);
                }
                break;
            case "x":
            default:
                // This requires the colWidth has already been calculated
                rowHeight = Math.max(0, this.layoutChild.calcHeight(this.colWidth));
                break;
        }

        this.rowHeight = rowHeight;
    },

    /**
     * Calculates how many cols will fit within the `maxGridWidth` and `maxCols`
     * boundaries.
     *
     * The grid will not be allowed to expand beyond this amount of columns.
     *
     * @private
     */
    calcMaxCols: function() {
        var colWidth     = this.colWidth,
            maxGridWidth = this.getOption("maxGridWidth"),
            maxCols      = this.getOption("maxCols");

        if (colWidth === 0 || maxGridWidth <= 0 || maxCols <= 0) {
            maxCols = 0;
        }
        else {
            // Adding a tiny amount to the amount of columns that fit within the
            // available width prevents issues with floating-point precision.
            maxCols = Math.min(Math.floor(maxGridWidth / colWidth + 0.000001), maxCols);
        }

        this.maxCols = maxCols;
    },

    /**
     * Calculates how many cols will fit within the `maxGridHeight` and
     * `maxRows` boundaries.
     *
     * The grid will not be allowed to expand beyond this amount of rows.
     *
     * @private
     */
    calcMaxRows: function() {
        var rowHeight     = this.rowHeight,
            maxGridHeight = this.getOption("maxGridHeight"),
            maxRows       = this.getOption("maxRows");

        if (rowHeight === 0 || maxGridHeight <= 0 || maxRows <= 0) {
            maxRows = 0;
        }
        else {
            // Adding a tiny amount to the amount of columns that fit within the
            // available width prevents issues with floating-point precision.
            maxRows = Math.min(Math.floor(maxGridHeight / rowHeight + 0.000001), maxRows);
        }

        this.maxRows = maxRows;
    },

    /**
     * Gets the amount of placeholders needed to fill a viewport of the provided
     * dimensions.
     *
     * @param  {number}  width   The width
     * @param  {number}  height  The height
     * @return {number}  The amount of placeholders.
     * @public
     */
    getPlaceholderCount: function(width, height) {
        var rowHeight = this.rowHeight,
            colWidth  = this.colWidth,
            cols,
            rows;

        if (colWidth === 0 || rowHeight === 0 || width <= 0 || height <= 0) {
            return 0;
        }

        cols = Math.min(this.getCols(), Math.ceil(width / colWidth) + 1);
        rows = Math.min(this.getRows(), Math.ceil(height / rowHeight) + 1);

        return Math.min(this.repeater.length, cols * rows);
    },

    /**
     * Calculates the amount of columns/rows arranged along the `primaryAxis`
     * for the current repeater length.
     *
     * If the `primaryAxis` has no maximum amount of cells, we divide the length
     * by the amount of cells along the other axis.
     *
     * If neither axis is finite, there is no breakpoint.
     *
     * @return {number}  The amount of columns/rows.
     * @protected
     */
    getBreakpoint: function() {
        var primaryAxis = this.getOption("primaryAxis") || "x",
            length      = this.repeater.length || 0,
            maxCols     = this.maxCols,
            maxRows     = this.maxRows;

        if (length <= 0 || maxCols === 0 || maxRows === 0) {
            return 0;
        }

        if (primaryAxis === "x") {
            if (isFinite(maxCols)) {
                return maxCols;
            }
            else if (isFinite(maxRows)) {
                return Math.ceil(length / maxRows);
            }
            else {
                return Infinity;
            }
        }
        else {
            if (isFinite(maxRows)) {
                return maxRows;
            }
            else if (isFinite(maxCols)) {
                return Math.ceil(length / maxCols);
            }
            else {
                return Infinity;
            }
        }
    },

    /**
     * Calculates the amount of columns needed to arrange the current dataset.
     *
     * @return {number}  The amount of columns.
     * @protected
     */
    getCols: function() {
        var primaryAxis = this.getOption("primaryAxis"),
            length      = this.repeater.length || 0,
            breakpoint  = this.getBreakpoint();

        if (primaryAxis === "x") {
            return isFinite(breakpoint) ? Math.min(breakpoint, length) : length;
        }
        else {
            if (isFinite(breakpoint)) {
                return breakpoint > 0 ? Math.ceil(length / breakpoint) : 0;
            }
            else {
                return length > 0 ? 1 : 0;
            }
        }
    },

    /**
     * Calculates the amount of rows for te current repeater length.
     *
     * @return {number}  The amount of rows.
     * @protected
     */
    getRows: function() {
        var primaryAxis = this.getOption("primaryAxis"),
            length      = this.repeater.length || 0,
            breakpoint  = this.getBreakpoint();

        if (primaryAxis === "x") {
            if (isFinite(breakpoint)) {
                return breakpoint > 0 ? Math.ceil(length / breakpoint) : 0;
            }
            else {
                return length > 0 ? 1 : 0;
            }
        }
        else {
            return isFinite(breakpoint) ? Math.min(breakpoint, length) : length;
        }
    },

    /**
     * Calculates in which column the last item is located.
     *
     * @return {number}  The column number (1-based).
     * @protected
     */
    getLastCol: function() {
        var primaryAxis = this.getOption("primaryAxis") || "x",
            cols        = this.getCols(),
            lastCol,
            length;

        if (primaryAxis === "x") {
            length  = this.repeater.length || 0;
            lastCol = length % cols;

            return lastCol > 0 ? lastCol : cols;
        }

        return cols;
    },

    /**
     * Calculates in which row the last item is located.
     *
     * @return {number}  The row number (1-based).
     * @protected
     */
    getLastRow: function() {
        var primaryAxis = this.getOption("primaryAxis") || "x",
            rows        = this.getRows(),
            lastRow,
            length;

        if (primaryAxis === "y") {
            length  = this.repeater.length || 0;
            lastRow = length % rows;

            return lastRow > 0 ? lastRow : rows;
        }

        return rows;
    },

    /**
     * Get the size of the entire arrangement.
     *
     * @return {Object}  The arrangement size.
     * @public
     */
    getArrangementSize: function() {
        var cols        = this.getCols(),
            rows        = this.getRows(),
            roundPixels = this.getOption("roundPixels"),
            bounds      = {};

        bounds.width  = cols * this.colWidth;
        bounds.height = rows * this.rowHeight;

        if (roundPixels) {
            bounds.width  = Math.floor(bounds.width);
            bounds.height = Math.floor(bounds.height);
        }

        return bounds;
    },

    /**
     * Gets the index by row and col.
     *
     * @param  {number}   col  The column number (1-based).
     * @param  {number}   row  The row number (1-based).
     * @return {?number}  The index if within bounds, `null` otherwise.
     * @protected
     */
    getIndexByColAndRow: function(col, row) {
        var primaryAxis     = this.getOption("primaryAxis") || "x",
            arrangementAxis = this.getOption("arrangementAxis") || "x",
            cols            = this.getCols(),
            rows            = this.getRows(),
            lastCol         = this.getLastCol(),
            lastRow         = this.getLastRow();

        if (col < 1 || row < 1 || col > cols || row > rows) {
            return null;
        }
        else if (primaryAxis === "x" && arrangementAxis === "x") {
            if (row < lastRow || (row === lastRow && col <= lastCol)) {
                return row * cols + col - cols - 1;
            }
        }
        else if (primaryAxis === "x" && arrangementAxis === "y") {
            if (col <= lastCol) {
                return col * rows + row - rows - 1;
            }

            if (row < lastRow) {
                return col * (rows - 1) + row - (rows - 1) - 1 + lastCol;
            }
        }
        else if (primaryAxis === "y" && arrangementAxis === "x") {
            if (row <= lastRow) {
                return row * cols + col - cols - 1;
            }

            if (col < lastCol) {
                return row * (cols - 1) + col - (cols - 1) - 1 + lastRow;
            }
        }
        else if (primaryAxis === "y" && arrangementAxis === "y") {
            if (col < lastCol || (col === lastCol && row <= lastRow)) {
                return col * rows + row - rows - 1;
            }
        }

        return null;
    },

    /**
     * Gets the col by index (1-based).
     *
     * @param  {number}   index  The index
     * @return {?number}  The col number, will be `null` if `index` is
     *                    out-of-bounds.
     * @protected
     */
    getColByIndex: function(index) {
        var length          = this.repeater.length || 0,
            primaryAxis     = this.getOption("primaryAxis") || "x",
            arrangementAxis = this.getOption("arrangementAxis") || "x",
            cols            = this.getCols(),
            rows            = this.getRows(),
            lastCol         = this.getLastCol(),
            lastRow         = this.getLastRow(),
            lastIndex;

        if (index < 0 || index >= length || cols === 0 || rows === 0) {
            return null;
        }
        else if (primaryAxis === "x" && arrangementAxis === "x") {
            return (index % cols) + 1;
        }
        else if (primaryAxis === "x" && arrangementAxis === "y") {
            lastIndex = lastCol * rows + lastRow - rows - 1;

            if (index <= lastIndex) {
                return Math.floor(index / lastRow) + 1;
            }

            index = index - lastIndex - 1;

            return Math.floor(index / (rows - 1)) + 1 + lastCol;
        }
        else if (primaryAxis === "y" && arrangementAxis === "x") {
            lastIndex = lastRow * cols + lastCol - cols - 1;

            if (index <= lastIndex) {
                return (index % lastCol) + 1;
            }

            index = index - lastIndex - 1;

            return (index % (cols - 1)) + 1;
        }
        else if (primaryAxis === "y" && arrangementAxis === "y") {
            return Math.floor(index / rows) + 1;
        }

        return null;
    },

    /**
     * Gets the row by index (1-based).
     *
     * @param  {number}   index  The index
     * @return {?number}  The row number, will be `null` if `index` is
     *                    out-of-bounds.
     * @protected
     */
    getRowByIndex: function(index) {
        var length          = this.repeater.length || 0,
            primaryAxis     = this.getOption("primaryAxis") || "x",
            arrangementAxis = this.getOption("arrangementAxis") || "x",
            cols            = this.getCols(),
            rows            = this.getRows(),
            lastCol         = this.getLastCol(),
            lastRow         = this.getLastRow(),
            lastIndex;

        if (index < 0 || index >= length || cols === 0 || rows === 0) {
            return null;
        }
        else if (primaryAxis === "x" && arrangementAxis === "x") {
            return Math.floor(index / cols) + 1;
        }
        else if (primaryAxis === "x" && arrangementAxis === "y") {
            lastIndex = lastCol * rows + lastRow - rows - 1;

            if (index <= lastIndex) {
                return (index % lastRow) + 1;
            }

            index = index - lastIndex - 1;

            return (index % (rows - 1)) + 1;
        }
        else if (primaryAxis === "y" && arrangementAxis === "x") {
            lastIndex = lastRow * cols + lastCol - cols - 1;

            if (index <= lastIndex) {
                return Math.floor(index / lastCol) + 1;
            }

            index = index - lastIndex - 1;

            return Math.floor(index / (cols - 1)) + 1 + lastRow;
        }
        else if (primaryAxis === "y" && arrangementAxis === "y") {
            return (index % rows) + 1;
        }

        return null;
    },

    /**
     * Gets a sorted set of indexes of children within the arrangement that
     * intersect with the provided bounds.
     *
     * The incoming bounds should be defined in pixels, relative to the top-left
     * corner of the childContainer.
     *
     * @param  {Object}  bounds         The bounds
     * @param  {number}  bounds.top     The top edge of the bounding box in
     *                                  pixels from the top of the grid
     * @param  {number}  bounds.left    The left edge of the bounding box in
     *                                  pixels from the left of the grid
     * @param  {number}  bounds.right   The right edge of the bounding box in
     *                                  pixels from the left of the grid
     * @param  {number}  bounds.bottom  The bottom edge of the bounding box in
     *                                  pixels from the top of the grid
     * @return {Array}   Array of indexes
     * @public
     */
    getArrangementIndexes: function(bounds) {
        var length    = this.repeater.length,
            colWidth  = this.colWidth,
            rowHeight = this.rowHeight,
            cols      = this.getCols(),
            rows      = this.getRows(),
            startCol,
            endCol    = cols,
            startRow,
            endRow    = rows,
            primaryAxis,
            col,
            row,
            index,
            indexes   = [];

        // No data
        if (!length) {
            return indexes;
        }

        // Zero-size grid
        if (colWidth === 0 || rowHeight === 0 || cols === 0 || rows === 0) {
            return indexes;
        }

        // Get column range
        startCol = bounds.left / colWidth;
        startCol = ~~startCol === startCol ? startCol + 1 : Math.ceil(startCol);
        endCol   = Math.min(cols, Math.ceil(bounds.right / colWidth));

        // Calculate row range
        startRow = bounds.top / rowHeight;
        startRow = ~~startRow === startRow ? startRow + 1 : Math.ceil(startRow);
        endRow   = Math.min(rows, Math.ceil(bounds.bottom / rowHeight));

        // Build index range
        primaryAxis = this.getOption("primaryAxis") || "x";

        if (primaryAxis === "x") {
            for (row = startRow; row <= endRow; row++) {
                for (col = startCol; col <= endCol; col++) {
                    index = this.getIndexByColAndRow(col, row);

                    if (index !== null) {
                        indexes.push(index);
                    }
                }
            }
        }
        else if (primaryAxis === "y") {
            for (col = startCol; col <= endCol; col++) {
                for (row = startRow; row <= endRow; row++) {
                    index = this.getIndexByColAndRow(col, row);

                    if (index !== null) {
                        indexes.push(index);
                    }
                }
            }
        }

        return indexes;
    },

    /**
     * Returns the distance (in pixels) between the top edge of the child
     * control at the given index, and the top edge of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getOffsetTop: function(index) {
        var row         = this.getRowByIndex(index) || 0,
            offset,
            roundPixels = this.getOption("roundPixels");

        offset = row > 1 ? (row - 1) * this.rowHeight : 0;

        return roundPixels ? Math.floor(offset) : offset;
    },

    /**
     * Returns the distance (in pixels) between the left edge of the child
     * control at the given index, and the left edge of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getOffsetLeft: function(index) {
        var col         = this.getColByIndex(index) || 0,
            offset,
            roundPixels = this.getOption("roundPixels");

        offset = col > 1 ? (col - 1) * this.colWidth : 0;

        return roundPixels ? Math.floor(offset) : offset;
    },

    /**
     * Gets the index of the child positioned closest to the provided
     * coordinates.
     *
     * The incoming coordinates should be defined in pixels, relative to the
     * top-left corner of the childContainer.
     *
     * @param  {number}  x  The coordinates on the `x` axis.
     * @param  {number}  y  The coordinates on the `y` axis.
     * @return {number}  The index
     * @public
     */
    getNearestIndex: function(x, y) {
        var cols        = this.getCols(),
            rows        = this.getRows(),
            primaryAxis = this.getOption("primaryAxis") || "x",
            col,
            colWidth    = this.colWidth,
            row,
            rowHeight   = this.rowHeight,
            index,
            lastCol     = this.getLastCol(),
            lastRow     = this.getLastRow(),
            distanceLeft,
            distanceUp;

        // If x- coordinate to the left of the grid, the closest index is
        // positioned directly to the right of it.
        x = x > 0 ? x : 0;

        // If y-coordinate is above the grid, the closest index is positioned
        // directly underneath it.
        y = y > 0 ? y : 0;

        // This calculates which cell of the grid contains the coordinates.
        // We're ignoring the bottom and right edges of the grid for now.
        col = colWidth > 0 ? Math.floor(x / colWidth) + 1 : 1;
        row = rowHeight > 0 ? Math.floor(y / rowHeight) + 1 : 1;

        // Try retrieving an index for this cell
        index = this.getIndexByColAndRow(col, row);

        // If this cell contains an index, we're done.
        if (index !== null) {
            return index;
        }

        // If both the last row and the last column have indexes arranged in
        // every single cell, we have a relatively simple problem space.
        if (lastCol === cols && lastRow === rows) {
            col = Math.min(col, cols);
            row = Math.min(row, rows);

            return this.getIndexByColAndRow(col, row);
        }

        // Sometimes, the last row/col has some empty cells, making things more
        // complicated.

        // All empty cells are in the last row
        if (primaryAxis === "x") {
            // If the cell is positioned below one of the indexes arranged on
            // the last row, the closest index is always positioned immediately
            // above the given coordinates.
            if (col <= lastCol && row > rows) {
                row = rows;
            }

            // If the cell is positioned to the right of the indexes arranged on
            // the last column, the closest index is always positioned
            // immediately to the left of the given coordinates.
            else if (row < rows && col > cols) {
                col = cols;
            }

            // If our cell is in one of the empty cells, the nearest index is
            // either directly above or directly to the left of the given
            // coordinates.
            else if (row === rows && col > lastCol && col <= cols) {
                distanceLeft = x - lastCol * colWidth;
                distanceUp   = y - (rows - 1) * rowHeight;

                if (distanceUp < distanceLeft) {
                    row = rows - 1;
                }
                else {
                    col = lastCol;
                }
            }

            // If the cell is positioned directly below an empty cell, the
            // nearest index is either directly above the given coordinates or
            // the right-most index to be arranged in the last row.
            else if (row > rows && col > lastCol && col <= cols) {
                distanceLeft = Math.sqrt(Math.pow(x - (lastCol * colWidth), 2) + Math.pow(y - (rows * rowHeight), 2));
                distanceUp   = y - (rows - 1) * rowHeight;

                if (distanceUp < distanceLeft) {
                    row = rows - 1;
                }
                else {
                    col = lastCol;
                    row = rows;
                }
            }

            // If the cell is positioned directly to the right of an empty cell,
            // the nearest index is either directly to the left of the given
            // coordinates or the bottom-most index to be arranged in the last
            // column.
            else if (row === rows && col > cols) {
                distanceLeft = x - lastCol * colWidth;
                distanceUp   = Math.sqrt(Math.pow(x - (cols * colWidth), 2) + Math.pow(y - ((rows - 1) * rowHeight), 2));

                if (distanceLeft < distanceUp) {
                    col = lastCol;
                }
                else {
                    col = cols;
                    row = rows - 1;
                }
            }

            // The coordinates are positioned diagonally to the bottom-right of
            // the closest index. The closest cell is either the bottom-most
            // cell in the last column or the right-most cell in the last row.
            else {
                distanceLeft = Math.sqrt(Math.pow(x - (lastCol * colWidth), 2) + Math.pow(y - (rows * rowHeight), 2));
                distanceUp   = Math.sqrt(Math.pow(x - (cols * colWidth), 2) + Math.pow(y - ((rows - 1) * rowHeight), 2));

                if (distanceLeft < distanceUp) {
                    col = lastCol;
                    row = rows;
                }
                else {
                    col = cols;
                    row = rows - 1;
                }
            }
        }

        // All unused cells are in the last column
        else {
            // If the cell is positioned below one of the indexes arranged on
            // the last row, the closest index is always positioned immediately
            // above the given coordinates.
            if (col < cols && row > rows) {
                row = rows;
            }

            // If the cell is positioned to the right of the indexes arranged on
            // the last column, the closest index is always positioned
            // immediately to the left of the given coordinates.
            else if (row <= lastRow && col > cols) {
                col = cols;
            }

            // If our cell is in one of the empty cells, the nearest index is
            // either directly above or directly to the left of the given
            // coordinates.
            else if (col === cols && row > lastRow && row <= rows) {
                distanceLeft = x - (cols - 1) * colWidth;
                distanceUp   = y - lastRow * rowHeight;

                if (distanceLeft < distanceUp) {
                    col = cols - 1;
                }
                else {
                    row = lastRow;
                }
            }

            // If the cell is positioned directly to the right an empty cell,
            // the nearest index is either directly to the left of the given
            // coordinates or the top-most index to be arranged in the last
            // column.
            else if (col > cols && row > lastRow && row <= rows) {
                distanceLeft = x - (cols - 1) * colWidth;
                distanceUp   = Math.sqrt(Math.pow(x - (cols * colWidth), 2) + Math.pow(y - (lastRow * rowHeight), 2));

                if (distanceLeft < distanceUp) {
                    col = cols - 1;
                }
                else {
                    col = cols;
                    row = lastRow;
                }
            }

            // If the cell is positioned directly below an empty cell, the
            // nearest index is either directly above the given coordinates or
            // the right-most index to be arranged in the last row.
            else if (col === cols && row > rows) {
                distanceLeft = Math.sqrt(Math.pow(x - ((cols - 1) * colWidth), 2) + Math.pow(y - (rows * rowHeight), 2));
                distanceUp   = y - lastRow * rowHeight;

                if (distanceUp < distanceLeft) {
                    row = lastRow;
                }
                else {
                    col = cols - 1;
                    row = rows;
                }
            }

            // The coordinates are positioned diagonally to the bottom-right of
            // the closest index. The closest cell is either the bottom-most
            // cell in the last column or the right-most cell in the last row.
            else {
                distanceLeft = Math.sqrt(Math.pow(x - ((cols - 1) * colWidth), 2) + Math.pow(y - (rows * rowHeight), 2));
                distanceUp   = Math.sqrt(Math.pow(x - (cols * colWidth), 2) + Math.pow(y - (lastRow * rowHeight), 2));

                if (distanceUp < distanceLeft) {
                    col = cols;
                    row = lastRow;
                }
                else {
                    col = cols - 1;
                    row = rows;
                }
            }
        }

        return this.getIndexByColAndRow(col, row);

    },

    /**
     * Returns the scroll offset (in pixels) that would align the top edge of
     * the child arranged at the provided index with the top edge of a scrolled
     * view of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getScrollTop: function(index) {
        return this.getOffsetTop(index);
    },

    /**
     * Returns the scroll offset (in pixels) that would align the left edge of
     * the child arranged at the provided index with the left edge of a scrolled
     * view of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getScrollLeft: function(index) {
        return this.getOffsetLeft(index);
    },

    /**
     * All children have the same size, setting the size for any particular
     * index is not applicable to this arranger.
     *
     * @return {boolean}  The size for this index did not change.
     * @protected
     */
    setSizeAtIndex: function() {
        return false;
    },

    /**
     * All children have the same size, unsetting the size for any particular
     * index is not applicable to this arranger.
     *
     * @return {boolean}  The size for this index did not change.
     * @protected
     */
    unsetSizeAtIndex: function() {
        return false;
    },

    /**
     * All children have the same size.
     *
     * @return {Object}  The size at index.
     * @protected
     */
    getSizeAtIndex: function() {
        var roundPixels = this.getOption("roundPixels"),
            colWidth    = this.colWidth,
            rowHeight   = this.rowHeight;

        return {
            width:  roundPixels ? Math.floor(colWidth) : colWidth,
            height: roundPixels ? Math.floor(rowHeight) : rowHeight
        };
    },

    /**
     * Renders the child if needed and sizes it.
     *
     * @param {enyo.Control}  child  The child
     * @public
     */
    drawChild: function(child) {
        var childContainer = this.repeater.getChildContainer(),
            rendered       = child.hasNode(),
            roundPixels    = this.getOption("roundPixels"),
            colWidth       = this.colWidth,
            rowHeight      = this.rowHeight,
            childBounds;

        // Determine the child's bounds
        childBounds = {
            width:  roundPixels ? Math.floor(colWidth) : colWidth,
            height: roundPixels ? Math.floor(rowHeight) : rowHeight
        };

        // Size child
        child.setBounds(childBounds);

        // For use internal to the arranger.
        child.set("repeaterSize", childBounds);

        // For use outside of the arranger.
        child.set("repeaterBounds", childBounds);

        // Render child as needed.
        if (childContainer.hasNode() && !rendered) {
            child.canGenerate = true;
            child.addStyles({
                position: "absolute",
                top:      0,
                left:     0
            });
            child.render();
        }
    }

});
